"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HowForestryMulchingIsAchieved() {
  const {
    ref: headlineRef,
    isVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.3
  });
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const ribbonRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const offsetRef = useRef(0);
  const steps = [{
    number: "01",
    title: "ASSESSMENT",
    description: "We evaluate terrain and vegetation to plan the most efficient path.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/forestry_site_assessment.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/forestry_site_assessment.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/forestry_site_assessment.png-wvc-sizes"
  }, {
    number: "02",
    title: "CLEARING",
    description: "High-torque mulchers shred invasive brush and small trees instantly.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/mulcher_in_action.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/mulcher_in_action.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/mulcher_in_action.png-wvc-sizes"
  }, {
    number: "03",
    title: "GRINDING",
    description: "Debris is processed into a fine, nutrient-rich organic layer.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/mulch_texture_detail.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/mulch_texture_detail.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/mulch_texture_detail.png-wvc-sizes"
  }, {
    number: "04",
    title: "LEVELING",
    description: "The machine passes over the mulch to create a smooth, walkable surface.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/land_leveling_process.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/land_leveling_process.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/land_leveling_process.png-wvc-sizes"
  }, {
    number: "05",
    title: "PROTECTION",
    description: "The mulch layer prevents soil erosion and suppresses weed regrowth.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/soil_erosion_protection.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/soil_erosion_protection.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/soil_erosion_protection.png-wvc-sizes"
  }, {
    number: "06",
    title: "RESTORATION",
    description: "Nutrients return to the earth, fostering a healthier ecosystem.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/restored_forest_floor.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/restored_forest_floor.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/restored_forest_floor.png-wvc-sizes"
  }, {
    number: "07",
    title: "FINISHING",
    description: "A final inspection ensures your land is pristine and ready for use.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/completed_land_clearing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/completed_land_clearing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678813/img/completed_land_clearing.png-wvc-sizes"
  }];
  const duplicatedSteps = [...steps, ...steps];
  useEffect(() => {
    let animationId: number;
    const speed = 0.4;
    const animate = () => {
      if (ribbonRef.current && !isPaused) {
        offsetRef.current -= speed;
        const trackWidth = ribbonRef.current.scrollWidth / 2;
        if (Math.abs(offsetRef.current) >= trackWidth) {
          offsetRef.current = 0;
        }
        ribbonRef.current.style.transform = `translateX(${offsetRef.current}px)`;
      }
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused]);
  const headlineWords = [{
    text: "Precision",
    italic: false,
    highlight: false
  }, {
    text: "clearing",
    italic: false,
    highlight: false
  }, {
    text: "that",
    italic: false,
    highlight: false
  }, {
    text: "nourishes",
    italic: true,
    highlight: true
  }, {
    text: "the",
    italic: false,
    highlight: false
  }, {
    text: "earth",
    italic: false,
    highlight: false
  }, {
    text: "below.",
    italic: false,
    highlight: false
  }];
  const decorativeDots = Array.from({
    length: 24
  }, (_, i) => ({
    top: `${i * 37 % 90 + 5}%`,
    left: `${i * 53 % 92 + 4}%`,
    size: i % 3 + 2
  }));
  return <section data-section-id="4914" id="how-forestry-mulching-is-achieved" className="bg-background text-foreground relative overflow-hidden py-[180px] px-4">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.15]" aria-hidden="true">
        {decorativeDots.map((dot, i) => <span key={i} data-index={i} className="absolute rounded-md bg-primary" style={{
        top: dot.top,
        left: dot.left,
        width: `${dot.size}px`,
        height: `${dot.size}px`
      }} />)}
      </div>

      <div className="relative mx-auto max-w-7xl">
        {}
        <div ref={eyebrowRef} className={`mb-12 text-center transition-all duration-1000 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs sm:text-sm tracking-[0.3em] text-secondary uppercase font-semibold">
            The Process — 07 Steps
          </span>
          <div className="mx-auto mt-6 h-px w-16 bg-primary/40" />
        </div>

        {}
        <h2 ref={headlineRef} className="font-default mx-auto max-w-5xl text-center text-4xl sm:text-6xl md:text-7xl lg:text-[80px] leading-[1.1] font-bold text-foreground">
          {headlineWords.map((word, i) => <span key={i} data-index={i} className="inline-block overflow-hidden align-baseline">
              <span className={`inline-block transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[110%]"} ${word.italic ? "italic font-serif" : ""} ${word.highlight ? "text-primary" : ""}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                {word.text}
                {i < headlineWords.length - 1 && "\u00A0"}
              </span>
            </span>)}
        </h2>

        {}
        <div className="relative mt-24 sm:mt-32">
          {}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 sm:w-32 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 sm:w-32 bg-gradient-to-l from-background to-transparent" />

          <div className="overflow-hidden" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
            <div ref={ribbonRef} className="flex gap-6 will-change-transform" style={{
            width: "max-content"
          }}>
              {duplicatedSteps.map((step, i) => <article key={i} data-index={i} className="w-[280px] sm:w-[320px] shrink-0 rounded-lg border border-border bg-card shadow-md p-8 transition-all duration-500 hover:border-primary/40 hover:shadow-lg">
                  <div className="flex items-start justify-between mb-8">
                    <span className="font-default text-3xl font-bold text-primary/90">
                      {step.number}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.25em] text-secondary uppercase mt-3 font-bold">
                      Phase
                    </span>
                  </div>

                  <div className="mb-8 flex justify-center">
                    <div className="size-24 overflow-hidden rounded-md border border-border bg-muted">
                      <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.title} loading="lazy" decoding="async" className="size-full object-cover" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="h-px w-6 bg-primary" />
                      <h3 className="font-default text-sm tracking-wider text-foreground uppercase font-bold">
                        {step.title}
                      </h3>
                    </div>
                    <p className="font-default text-base leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </article>)}
            </div>
          </div>
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 sm:mt-24 flex justify-center transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="outline" size="lg" className="group h-14 px-10 rounded-md border-primary/30 bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary font-default text-lg font-semibold tracking-wide shadow-sm">
            <Link to="/ServicesPage">
              Explore Our Full Services
              <ArrowRight className="ml-2 size-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}